import { useState, useMemo } from 'react';
import { useQuery } from '@apollo/client';
import { useSearchParams, Link } from 'react-router-dom';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { Input, Label } from '@artstorefronts/ui';
import { ChevronLeft } from 'lucide-react';
import AdminLayout from '../../../components/AdminLayout';
import { GET_ONBOARDING_FUNNEL, GET_ONBOARDING_STEP_REGISTRY } from '../../../graphql/queries';
import { GET_ONBOARDING_VERSION } from './stepMappers';
import { migrateStepConfigOverrides } from './migrateStepOverrides';
import type { RegistryConfigField } from './StepConfigForm';

interface StepDiff {
  stepCode: string;
  kind: 'same' | 'added' | 'removed' | 'reordered' | 'config_changed';
  leftPosition?: number;
  rightPosition?: number;
}

function normalizeForCompare(stepCode: string, raw: unknown): Record<string, unknown> {
  const o = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : {};
  return migrateStepConfigOverrides(stepCode, o);
}

function computeStepDiff(leftSteps: any[], rightSteps: any[]): Map<string, StepDiff> {
  const diffMap = new Map<string, StepDiff>();
  const leftMap = new Map(leftSteps?.map((s) => [s.stepCode, s]) || []);
  const rightMap = new Map(rightSteps?.map((s) => [s.stepCode, s]) || []);

  const allCodes = new Set([...leftMap.keys(), ...rightMap.keys()]);

  allCodes.forEach((code) => {
    const left = leftMap.get(code);
    const right = rightMap.get(code);

    if (!left && right) {
      diffMap.set(code, { stepCode: code, kind: 'added', rightPosition: right.position });
    } else if (left && !right) {
      diffMap.set(code, { stepCode: code, kind: 'removed', leftPosition: left.position });
    } else if (left && right) {
      const leftNorm = normalizeForCompare(code, left.configOverrides);
      const rightNorm = normalizeForCompare(code, right.configOverrides);
      const configChanged = JSON.stringify(leftNorm) !== JSON.stringify(rightNorm);
      const positionChanged = left.position !== right.position;

      if (configChanged) {
        diffMap.set(code, {
          stepCode: code,
          kind: 'config_changed',
          leftPosition: left.position,
          rightPosition: right.position,
        });
      } else if (positionChanged) {
        diffMap.set(code, {
          stepCode: code,
          kind: 'reordered',
          leftPosition: left.position,
          rightPosition: right.position,
        });
      } else {
        diffMap.set(code, {
          stepCode: code,
          kind: 'same',
          leftPosition: left.position,
          rightPosition: right.position,
        });
      }
    }
  });

  return diffMap;
}

function buildConfigLabelMap(entries: { code: string; configSchema: RegistryConfigField[] }[]): Map<string, string> {
  const m = new Map<string, string>();
  for (const e of entries) {
    for (const f of e.configSchema) {
      m.set(`${e.code}:${f.key}`, f.label);
    }
  }
  return m;
}

function VersionColumn({ version, funnel, diff }: { version: any; funnel: any; diff: Map<string, StepDiff> }) {
  const chartData =
    funnel?.steps?.map((step: any) => ({
      stepCode: step.stepCode,
      viewed: step.viewedCount,
      submitted: step.submittedCount,
    })) || [];

  const getBadgeColor = (stepCode: string) => {
    const diffInfo = diff.get(stepCode);
    if (!diffInfo) return 'bg-gray-100 text-gray-800';
    if (diffInfo.kind === 'added') return 'bg-green-100 text-green-800';
    if (diffInfo.kind === 'removed') return 'bg-red-100 text-red-800';
    if (diffInfo.kind === 'reordered') return 'bg-orange-100 text-orange-800';
    if (diffInfo.kind === 'config_changed') return 'bg-yellow-100 text-yellow-800';
    return 'bg-gray-100 text-gray-800';
  };

  const getBadgeText = (stepCode: string) => {
    const diffInfo = diff.get(stepCode);
    if (!diffInfo) return '';
    if (diffInfo.kind === 'added') return 'Added';
    if (diffInfo.kind === 'removed') return 'Removed';
    if (diffInfo.kind === 'reordered') return 'Reordered';
    if (diffInfo.kind === 'config_changed') return 'Config Changed';
    return '';
  };

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-lg font-semibold">
            {version?.name} (v{version?.version})
          </h3>
          {version?.isDefault && (
            <span className="px-2 py-1 text-xs font-medium bg-green-100 text-green-800 rounded-full">Default</span>
          )}
        </div>
        {funnel && (
          <div className="text-sm text-gray-600">
            <div>Total: {funnel.totalSessions} sessions</div>
            <div>Completion: {(funnel.completionRate * 100).toFixed(1)}%</div>
          </div>
        )}
      </div>

      <div className="bg-white rounded-lg shadow p-4">
        <h4 className="text-sm font-semibold mb-2">Steps</h4>
        <div className="space-y-1">
          {version?.steps?.map((step: any) => {
            const diffInfo = diff.get(step.stepCode);
            const badgeColor = getBadgeColor(step.stepCode);
            const badgeText = getBadgeText(step.stepCode);

            return (
              <div
                key={step.stepCode}
                className={`flex items-center justify-between p-2 rounded ${
                  diffInfo?.kind === 'removed' ? 'line-through opacity-50' : ''
                }`}
              >
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-500">{step.position}.</span>
                  <span className="text-sm font-medium">{step.stepCode}</span>
                  {badgeText && (
                    <span className={`px-2 py-0.5 text-xs font-medium rounded ${badgeColor}`}>{badgeText}</span>
                  )}
                </div>
                <span className={`text-xs ${step.isEnabled ? 'text-green-600' : 'text-gray-400'}`}>
                  {step.isEnabled ? 'Enabled' : 'Disabled'}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      <div className="bg-white rounded-lg shadow p-4">
        <h4 className="text-sm font-semibold mb-2">Funnel</h4>
        <ResponsiveContainer width="100%" height={200}>
          <BarChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="stepCode" angle={-45} textAnchor="end" height={80} fontSize={10} />
            <YAxis fontSize={10} />
            <Tooltip />
            <Legend />
            <Bar dataKey="viewed" fill="#9ca3af" name="Viewed" />
            <Bar dataKey="submitted" fill="#16a34a" name="Submitted" />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function OnboardingComparePage() {
  const [searchParams] = useSearchParams();
  const leftId = searchParams.get('left');
  const rightId = searchParams.get('right');
  const [filters, setFilters] = useState({});

  const { data: registryData } = useQuery(GET_ONBOARDING_STEP_REGISTRY);

  const { data: leftData } = useQuery(GET_ONBOARDING_VERSION, {
    variables: { id: leftId },
    skip: !leftId,
  });

  const { data: rightData } = useQuery(GET_ONBOARDING_VERSION, {
    variables: { id: rightId },
    skip: !rightId,
  });

  const { data: leftFunnel } = useQuery(GET_ONBOARDING_FUNNEL, {
    variables: { flowVersionId: leftId, filters },
    skip: !leftId,
  });

  const { data: rightFunnel } = useQuery(GET_ONBOARDING_FUNNEL, {
    variables: { flowVersionId: rightId, filters },
    skip: !rightId,
  });

  const diff = useMemo(
    () => computeStepDiff(leftData?.onboardingVersion?.steps, rightData?.onboardingVersion?.steps),
    [leftData, rightData]
  );

  const configLabelMap = useMemo(() => buildConfigLabelMap(registryData?.onboardingStepRegistry ?? []), [registryData]);

  const configDiffRows = useMemo(() => {
    const leftSteps = leftData?.onboardingVersion?.steps ?? [];
    const rightSteps = rightData?.onboardingVersion?.steps ?? [];
    const rows: {
      stepCode: string;
      key: string;
      label: string;
      left: unknown;
      right: unknown;
    }[] = [];

    for (const [code, info] of diff.entries()) {
      if (info.kind !== 'config_changed') continue;
      const left = leftSteps.find((s: any) => s.stepCode === code);
      const right = rightSteps.find((s: any) => s.stepCode === code);
      if (!left || !right) continue;
      const lo = normalizeForCompare(code, left.configOverrides);
      const ro = normalizeForCompare(code, right.configOverrides);
      const keys = new Set([...Object.keys(lo), ...Object.keys(ro)]);
      for (const key of keys) {
        if (JSON.stringify(lo[key]) === JSON.stringify(ro[key])) continue;
        rows.push({
          stepCode: code,
          key,
          label: configLabelMap.get(`${code}:${key}`) ?? key,
          left: lo[key],
          right: ro[key],
        });
      }
    }
    return rows;
  }, [diff, leftData, rightData, configLabelMap]);

  if (!leftId || !rightId) {
    return (
      <AdminLayout title="Compare Versions" description="Missing version IDs in URL">
        <div className="text-red-500">Please provide both left and right version IDs in the URL parameters.</div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Compare Onboarding Versions" description="Side-by-side version comparison with shared filters">
      <div className="space-y-6">
        <Link
          to="/admin/onboarding"
          className="inline-flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900"
        >
          <ChevronLeft className="w-4 h-4" />
          Back to versions
        </Link>

        <div className="bg-white rounded-lg shadow p-4">
          <h3 className="text-sm font-semibold mb-3">Cohort Filters (applies to both)</h3>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label htmlFor="compare-intent" className="block text-xs mb-1">
                Primary Intent
              </Label>
              <Input
                id="compare-intent"
                type="text"
                placeholder="Comma-separated"
                className="w-full text-sm"
                onChange={(e) => {
                  const value = e.target.value;
                  setFilters((prev) => ({
                    ...prev,
                    intents: value ? value.split(',').map((s) => s.trim()) : undefined,
                  }));
                }}
              />
            </div>
            <div>
              <Label htmlFor="compare-career-stage" className="block text-xs mb-1">
                Career Stage
              </Label>
              <Input
                id="compare-career-stage"
                type="text"
                placeholder="Comma-separated"
                className="w-full text-sm"
                onChange={(e) => {
                  const value = e.target.value;
                  setFilters((prev) => ({
                    ...prev,
                    careerStages: value ? value.split(',').map((s) => s.trim()) : undefined,
                  }));
                }}
              />
            </div>
            <div>
              <Label htmlFor="compare-started-after" className="block text-xs mb-1">
                Started After
              </Label>
              <Input
                id="compare-started-after"
                type="date"
                className="w-full text-sm"
                onChange={(e) => {
                  setFilters((prev) => ({
                    ...prev,
                    startedAfter: e.target.value ? new Date(e.target.value).toISOString() : undefined,
                  }));
                }}
              />
            </div>
          </div>
        </div>

        {configDiffRows.length > 0 && (
          <div className="bg-white rounded-lg shadow p-4">
            <h3 className="text-sm font-semibold mb-3">Config override differences</h3>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="text-left text-gray-500 border-b">
                    <th className="py-2 pr-4">Step</th>
                    <th className="py-2 pr-4">Field</th>
                    <th className="py-2 pr-4">Left</th>
                    <th className="py-2">Right</th>
                  </tr>
                </thead>
                <tbody>
                  {configDiffRows.map((r) => (
                    <tr key={`${r.stepCode}-${r.key}`} className="border-b border-gray-100 align-top">
                      <td className="py-2 pr-4 font-mono text-xs">{r.stepCode}</td>
                      <td className="py-2 pr-4">{r.label}</td>
                      <td className="py-2 pr-4 font-mono text-xs whitespace-pre-wrap break-all">
                        {JSON.stringify(r.left)}
                      </td>
                      <td className="py-2 font-mono text-xs whitespace-pre-wrap break-all">
                        {JSON.stringify(r.right)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <div className="grid grid-cols-2 gap-6">
          <VersionColumn version={leftData?.onboardingVersion} funnel={leftFunnel?.onboardingFunnel} diff={diff} />
          <VersionColumn version={rightData?.onboardingVersion} funnel={rightFunnel?.onboardingFunnel} diff={diff} />
        </div>
      </div>
    </AdminLayout>
  );
}

export default OnboardingComparePage;
